import { create } from 'zustand';
import dayjs from 'dayjs';
import { BookingDto, BookingsApi, BookingSummaryDto } from '@/api/generated';
import { api } from '@/api/api';

interface DashboardState {
  summary: BookingSummaryDto | null;
  bookings: BookingDto[];
  weekBookings: BookingDto[]; // Rezervace pro celý týden
  isLoading: boolean;
  isLoadingBookings: boolean;
  error: string | null;
  selectedDate: string;
  salonId: string | null;
  viewMode: 'day' | 'week' | 'fullWeek';
}

interface DashboardActions {
  fetchSummary: (date: string, salonId?: string) => Promise<void>;
  fetchWeekBookings: (date: string, salonId?: string, viewMode?: 'day' | 'week' | 'fullWeek') => Promise<void>;
  fetchBookings: () => Promise<void>;
  setSelectedDate: (date: string) => void;
  setSalonId: (salonId: string) => void;
  setViewMode: (viewMode: 'day' | 'week' | 'fullWeek') => void;
  clearError: () => void;
}

type DashboardStore = DashboardState & DashboardActions;

const bookingsApi = new BookingsApi(api);

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  // State
  summary: null,
  bookings: [],
  weekBookings: [],
  isLoading: false,
  isLoadingBookings: false,
  error: null,
  selectedDate: dayjs().format('YYYY-MM-DD'), // Dnešní datum ve formátu YYYY-MM-DD
  salonId: null,
  viewMode: 'day',

  // Actions
  fetchSummary: async (date: string, salonId?: string, viewMode: 'day' | 'week' | 'fullWeek' = 'day') => {
    set({ isLoading: true, isLoadingBookings: true, error: null });

    try {
      const selectedDate = dayjs(date);
      let dateFrom: string;
      let dateTo: string;

      if (viewMode === 'day') {
        // Pro den načteme pouze ten den
        dateFrom = selectedDate.format('YYYY-MM-DD');
        dateTo = selectedDate.format('YYYY-MM-DD');
      } else {
        // Pro týden načteme celý týden
        const weekStart = selectedDate.startOf('week');
        const weekEnd = selectedDate.endOf('week');
        dateFrom = weekStart.format('YYYY-MM-DD');
        dateTo = weekEnd.format('YYYY-MM-DD');
      }

      console.log(`📊 Načítání summary pro statistiky: ${dateFrom} - ${dateTo} (režim: ${viewMode})`);

      const response = await bookingsApi.getBookingSummary({
        salonId,
        dateFrom,
        dateTo
      });

      set({
        summary: response.data,
        bookings: response.data.bookings || [], // Rezervace jsou součástí summary
        isLoading: false,
        isLoadingBookings: false,
        error: null
      });
    } catch (error: any) {
      console.error('Chyba při načítání dashboard sumarizace:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se načíst data pro dashboard',
        isLoading: false,
        isLoadingBookings: false
      });
    }
  },

  fetchWeekBookings: async (date: string, salonId?: string, viewMode: 'day' | 'week' | 'fullWeek' = 'day') => {
    if (!salonId) return;

    set({ isLoadingBookings: true, error: null });

    try {
      const selectedDate = dayjs(date);
      let dateFrom: string;
      let dateTo: string;

      if (viewMode === 'day') {
        // Pro den načteme pouze ten den
        dateFrom = selectedDate.format('YYYY-MM-DD');
        dateTo = selectedDate.format('YYYY-MM-DD');
      } else {
        // Pro týden načteme celý týden
        const weekStart = selectedDate.startOf('week');
        const weekEnd = selectedDate.endOf('week');
        dateFrom = weekStart.format('YYYY-MM-DD');
        dateTo = weekEnd.format('YYYY-MM-DD');
      }

      console.log(`📅 Načítání summary pro interval: ${dateFrom} - ${dateTo} (režim: ${viewMode})`);

      const response = await bookingsApi.getBookingSummary({
        salonId,
        dateFrom,
        dateTo
      });

      set({
        weekBookings: response.data.bookings || [],
        isLoadingBookings: false,
        error: null
      });

      console.log(`✅ Načteno ${response.data.bookings?.length || 0} rezervací pro týden ze summary`);
    } catch (error: any) {
      console.error('Chyba při načítání summary pro týden:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se načíst summary pro týden',
        isLoadingBookings: false
      });
    }
  },

  fetchBookings: async () => {
    // Tento endpoint už není potřeba, všechna data jsou v summary
    console.warn('fetchBookings je deprecated, používej fetchSummary');
  },


  setSelectedDate: (date: string) => {
    set({ selectedDate: date });
    // Automaticky načteme nová data při změně data
    const { salonId } = get();
    get().fetchSummary(date, salonId || undefined);
  },

  setSalonId: (salonId: string) => {
    set({ salonId });
    // Automaticky načteme nová data při změně salonu
    const { selectedDate } = get();
    get().fetchSummary(selectedDate, salonId);
  },

  setViewMode: (viewMode: 'day' | 'week' | 'fullWeek') => {
    set({ viewMode });
    // Automaticky načteme nová data při změně režimu
    const { selectedDate, salonId } = get();
    if (salonId) {
      get().fetchWeekBookings(selectedDate, salonId, viewMode);
    }
  },

  clearError: () => {
    set({ error: null });
  }
}));
